import type { Translate, TranslationKey } from '~/shared/internationalization/translation';

import { automationAssessmentTranslationKeys } from '../profile-tasks/automation-assessment/automation-assessment-translations';
import { getProfileTaskClassificationTranslationKey } from '../profile-tasks/profile-task-translations';
import type { PotentialBand } from './task-cassette-model';

const prefix = 'pages.job-architecture-v2.profile-tasks.automation-recipes.cassette' as const;

/** The cassette's own keys; everything else it prints reuses the tiles' and the AI transformation page's. */
const taskCassetteTranslationKeys = {
    effortHours: `${prefix}.effort-hours`,
    effortRange: `${prefix}.effort-range`,
    etchingKicker: `${prefix}.etching-kicker`,
    etchingTagline: `${prefix}.etching-tagline`,
    noWorkflow: `${prefix}.no-workflow`,
    perWeek: `${prefix}.per-week`,
    task: `${prefix}.task`,
    workflowSteps: `${prefix}.workflow-steps`,
} as const satisfies Record<string, TranslationKey>;

const bandKey = (band: PotentialBand | 'no-data'): TranslationKey => `pages.ai-transformation.band.${band}`;

/** Every text printed on the cassette, translated once and handed to the pure print functions. */
export type TaskCassetteLabels = {
    readonly automationPotential: string;
    readonly bands: Readonly<Record<PotentialBand, string>>;
    readonly contribution: string;
    readonly effort: (minimum: number, maximum: number) => string;
    readonly etchingKicker: string;
    readonly etchingTagline: string;
    readonly noWorkflow: string;
    readonly notAssessed: string;
    readonly perWeek: string;
    readonly task: string;
    readonly workflowSteps: string;
};

export const taskCassetteLabels = (translate: Translate): TaskCassetteLabels => ({
    automationPotential: translate(automationAssessmentTranslationKeys.title),
    bands: {
        high: translate(bandKey('high')),
        low: translate(bandKey('low')),
        moderate: translate(bandKey('moderate')),
        none: translate(bandKey('none')),
        'very-high': translate(bandKey('very-high')),
    },
    contribution: translate(getProfileTaskClassificationTranslationKey('contribution')),
    effort: (minimum, maximum) =>
        minimum === maximum
            ? translate(taskCassetteTranslationKeys.effortHours, { hours: minimum })
            : translate(taskCassetteTranslationKeys.effortRange, { maximum, minimum }),
    etchingKicker: translate(taskCassetteTranslationKeys.etchingKicker),
    etchingTagline: translate(taskCassetteTranslationKeys.etchingTagline),
    noWorkflow: translate(taskCassetteTranslationKeys.noWorkflow),
    notAssessed: translate(bandKey('no-data')),
    perWeek: translate(taskCassetteTranslationKeys.perWeek),
    task: translate(taskCassetteTranslationKeys.task),
    workflowSteps: translate(taskCassetteTranslationKeys.workflowSteps),
});
